import { handleActions } from "redux-actions";
import dayjs from "dayjs";

import {
  FETCH_MODAL_FILTERS_SUCCESS,
  FETCH_GOAL_BY_TYPE_SUCCESS,
  FETCH_CURRENT_USER_SUCCESS,
  FETCH_UPDATE_GOAL_SUCCESS,
  UPDATE_MODAL_FILTER,
  UPDATE_MODULE_VIEW,
  UPDATE_TOP_GOALS,
  UPDATE_DATE,
  UPDATE_VIEW,
  SET_MODAL_FILTERS_LOADING,
  SET_TOP_GOALS_LOADING,
  SET_VISIBLE_COLUMNS,
  SET_MODAL_LOADING,
  SET_GOALS_LOADING,
  SET_LOADING,
  SET_DATE,
  SET_STATUS,
  SET_ERROR,
  RESET_FILTERS
} from "./goalsSagas";

export const initialState = {
  currentUser: {},
  goalsLoading: {
    attendance: true,
    revenue: true,
    memberships: true,
    retention: true,
    daily_pulse: true
  },
  isLoading: true,
  goals: {},
  moduleViewOptions: [
    { id: "store", name: "Store" },
    { id: "staff", name: "Staff" }
  ],
  selectedModuleView: { id: "store", name: "Store" },
  topGoals: [],
  bottomGoals: [],
  topGoalsLoading: true,
  view: [
    { id: "individual", name: "Individual" },
    { id: "total", name: "Total" }
  ],
  selectedView: { id: "individual", name: "Individual" },
  date: "",
  isCurrentMonth: true,
  visibleColumns: {},
  addStaffModal: {
    isModalLoading: true,
    selectedFilters: {
      goals: [],
      staffs: [],
      divisions: [],
      districts: [],
      stores: []
    },
    filters: {},
    isFiltersLoading: {
      divisions: false,
      districts: false,
      stores: false,
      goals: false,
      staffs: false
    }
  },
  status: "idle",
  errors: null
};

const DISPLAY_TOP_GOALS_RULES = {
  1: [1, 0],
  2: [1, 1],
  3: [2, 1],
  default: [2, 2]
};

const VIEW_FILTER_OPTIONS = {
  default: [
    { id: "individual", name: "Individual" },
    { id: "total", name: "Total" }
  ],
  staff: (singleLocation) => [
    { id: "individual", name: "Individual" },
    { id: "store_total", name: "Store Total" },
    ...(!singleLocation ? [{ id: "all_stores_total", name: "All Stores Total" }] : [])
  ]
};

const getCheckedFilters = (data) => {
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      const checkedItems = value.filter((elem) => elem.checked);

      if (checkedItems.length > 0) {
        acc[key] = checkedItems;
      }
    }
    return acc;
  }, {});
};

function getTopAndBottomGoals(data, key = "progress") {
  const allItems = Object.entries(data)
    .filter(([, value]) => Array.isArray(value.goals))
    .flatMap(([sourceKey, { goals }]) =>
      goals.filter((goal) => goal[key] < 100 && goal.active).map((goal) => ({ ...goal, sourceKey }))
    );

  const sorted = [...allItems].sort((a, b) => a[key] - b[key]);

  const [topCount, leastCount] =
    DISPLAY_TOP_GOALS_RULES[sorted.length] || DISPLAY_TOP_GOALS_RULES.default;

  return {
    least_performing: sorted.slice(0, leastCount),
    top_performing: sorted.slice(-topCount).reverse()
  };
}

const getVisibleColumns = ({ selectedModuleView, selectedView, isCurrentMonth, currentUser }) => {
  const isIndividual = selectedView.id === "individual";
  const canModify = currentUser.can_modify_goals;
  const singleLocation = currentUser.singleLocation;

  const baseColumns = {
    showIncrease: isIndividual,
    showForecast: isCurrentMonth,
    showNeeded: isCurrentMonth,
    showDisplayDropdown: isIndividual,
    showModifyButton: isCurrentMonth && isIndividual && canModify
  };

  if (selectedModuleView.id === "staff") {
    return {
      ...baseColumns,
      showStaff: true,
      showAddStaffButton: isIndividual && isCurrentMonth && canModify
    };
  }

  return {
    ...baseColumns,
    showLocation: !singleLocation
  };
};

export default handleActions(
  {
    [RESET_FILTERS]: (state) => ({
      ...state,
      selectedModalFilters: initialState.selectedModalFilters,
      status: initialState.status,
      errors: initialState.errors
    }),
    [SET_LOADING]: (state, action) => ({
      ...state,
      isLoading: action.payload
    }),
    [SET_ERROR]: (state, action) => ({
      ...state,
      status: "failed",
      errors: action.payload
    }),
    [SET_STATUS]: (state, action) => ({
      ...state,
      status: action.payload
    }),
    [SET_MODAL_LOADING]: (state, action) => ({
      ...state,
      addStaffModal: {
        ...state.addStaffModal,
        isModalLoading: action.payload
      }
    }),
    [SET_MODAL_FILTERS_LOADING]: (state, action) => {
      const filterNames = Object.keys(state.addStaffModal.isFiltersLoading);
      const isFiltersLoading = filterNames.reduce((acc, key) => {
        acc[key] = action.payload.filters.includes(key) ? action.payload.isLoading : false;
        return acc;
      }, {});

      return {
        ...state,
        addStaffModal: {
          ...state.addStaffModal,
          isFiltersLoading
        }
      };
    },
    [SET_GOALS_LOADING]: (state, action) => ({
      ...state,
      goalsLoading: { ...state.goalsLoading, ...action.payload }
    }),
    [SET_TOP_GOALS_LOADING]: (state, action) => ({
      ...state,
      topGoalsLoading: action.payload
    }),
    [SET_VISIBLE_COLUMNS]: (state) => {
      const { selectedModuleView, selectedView, isCurrentMonth, currentUser } = state;

      return {
        ...state,
        visibleColumns: getVisibleColumns({
          selectedModuleView,
          selectedView,
          isCurrentMonth,
          currentUser
        })
      };
    },
    [UPDATE_MODAL_FILTER]: (state, action) => ({
      ...state,
      addStaffModal: {
        ...state.addStaffModal,
        selectedFilters: {
          ...state.addStaffModal.selectedFilters,
          ...action.payload.selectedFilter
        }
      }
    }),
    [FETCH_MODAL_FILTERS_SUCCESS]: (state, action) => {
      const checkedFilters = getCheckedFilters(action.payload);

      return {
        ...state,
        addStaffModal: {
          ...state.addStaffModal,
          filters: action.payload,
          selectedFilters: {
            ...checkedFilters,
            staffs: action.payload.staffs
          }
        }
      };
    },
    [SET_DATE]: (state, action) => ({
      ...state,
      date: action.payload
    }),
    [UPDATE_MODULE_VIEW]: (state, action) => ({
      ...state,
      selectedModuleView: action.payload,
      view:
        action.payload?.id === "staff"
          ? VIEW_FILTER_OPTIONS.staff(state.currentUser.singleLocation)
          : VIEW_FILTER_OPTIONS.default,
      selectedView: initialState.selectedView,
      date: state.currentUser.calendarEndDate,
      isCurrentMonth: initialState.isCurrentMonth,
      goals: {}
    }),
    [UPDATE_DATE]: (state, action) => {
      const currentDate = dayjs(state.currentUser.calendarEndDate);
      const isCurrentMonth = action.payload
        ? dayjs(action.payload).year() === currentDate.year() &&
          dayjs(action.payload).month() === currentDate.month()
        : false;

      return {
        ...state,
        date: action.payload,
        isCurrentMonth
      };
    },
    [UPDATE_VIEW]: (state, action) => ({
      ...state,
      selectedView: action.payload
    }),
    [FETCH_CURRENT_USER_SUCCESS]: (state, action) => ({
      ...state,
      currentUser: action.payload || state.currentUser
    }),
    [UPDATE_TOP_GOALS]: (state) => {
      const { top_performing, least_performing } = getTopAndBottomGoals(state.goals);
      return {
        ...state,
        topGoals: top_performing || state.topGoals,
        bottomGoals: least_performing || state.bottomGoals
      };
    },
    [FETCH_UPDATE_GOAL_SUCCESS]: (state, action) => {
      const [key, updatedGoal] = Object.entries(action.payload)[0];

      const goals = state.goals[key].goals.map((item) =>
        item.id === updatedGoal.id ? updatedGoal : item
      );

      const updatedGoals = {
        ...state.goals,
        [key]: {
          ...state.goals[key],
          goals
        }
      };

      const { top_performing, least_performing } = getTopAndBottomGoals(updatedGoals);

      return {
        ...state,
        goals: updatedGoals,
        topGoals: top_performing || state.topGoals,
        bottomGoals: least_performing || state.bottomGoals
      };
    },

    [FETCH_GOAL_BY_TYPE_SUCCESS]: (state, action) => {
      const { goal_type, goals, show_module } = action.payload;

      return {
        ...state,
        goals: {
          ...state.goals,
          [goal_type]: {
            goals: goals[goal_type],
            title: goals[`${goal_type}Title`],
            show_module
          }
        }
      };
    }
  },
  initialState
);
